/**
 * Google encoded polyline decode (same algorithm as Flutter / Maps SDK).
 * Also accepts an already-decoded array of {lat,lng} points.
 */
export function decodePolyline(encoded) {
  if (!encoded) return [];

  if (Array.isArray(encoded)) {
    return encoded
      .map((point) => {
        if (Array.isArray(point) && point.length >= 2) {
          const lat = Number.parseFloat(point[0]);
          const lng = Number.parseFloat(point[1]);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return { lat, lng };
        }
        const lat = Number.parseFloat(point?.lat ?? point?.latitude);
        const lng = Number.parseFloat(point?.lng ?? point?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
      })
      .filter(Boolean);
  }

  if (typeof encoded !== "string") return [];

  try {
    const points = [];
    let index = 0;
    let lat = 0;
    let lng = 0;

    while (index < encoded.length) {
      let b;
      let shift = 0;
      let result = 0;

      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      lat += result & 1 ? ~(result >> 1) : result >> 1;

      shift = 0;
      result = 0;

      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      lng += result & 1 ? ~(result >> 1) : result >> 1;

      points.push({ lat: lat * 1e-5, lng: lng * 1e-5 });
    }

    return points;
  } catch (error) {
    console.error("decodePolyline failed:", error);
    return [];
  }
}

/**
 * Encode [{ lat, lng }] to Google polyline string.
 */
export function encodePolyline(points) {
  if (!Array.isArray(points) || points.length === 0) return "";

  let lastLat = 0;
  let lastLng = 0;
  let result = "";

  const encodeValue = (value) => {
    let v = value < 0 ? ~(value << 1) : value << 1;
    let encoded = "";
    while (v >= 0x20) {
      encoded += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
      v >>= 5;
    }
    encoded += String.fromCharCode(v + 63);
    return encoded;
  };

  for (const point of points) {
    const lat = Math.round(Number(point.lat) * 1e5);
    const lng = Math.round(Number(point.lng) * 1e5);
    result += encodeValue(lat - lastLat);
    result += encodeValue(lng - lastLng);
    lastLat = lat;
    lastLng = lng;
  }

  return result;
}
